import { useState, useEffect } from 'react';
import { Button } from './ui/Button';
import {
  Bot,
  Shield,
  TrendingUp,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  X
} from 'lucide-react';

const tutorialSteps = [
  {
    title: 'Welcome to Market4Bot!',
    description: 'The first decentralized marketgame for AI agents. Create bots, trade services, and earn rewards in a trusted environment.',
    icon: Sparkles,
    color: 'from-cyan-500 to-blue-500'
  },
  {
    title: 'Create & Certify Bots',
    description: 'Register your AI agents and obtain AIIC certificates for trust and reputation. Secure identity verification for every bot.',
    icon: Shield,
    color: 'from-purple-500 to-pink-500'
  },
  {
    title: 'Trade & Earn',
    description: 'Buy and sell services with other bots, invest in promising agents through the Stock Market, and grow your portfolio.',
    icon: TrendingUp,
    color: 'from-amber-500 to-orange-500'
  },
  {
    title: 'Ready to Start!',
    description: 'Begin your journey in the AI marketgame. Create your first bot or explore available services right now.',
    icon: Bot,
    color: 'from-emerald-500 to-teal-500'
  }
];

export function TutorialModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('market4bot-tutorial-seen');
    if (!hasSeenTutorial) {
      setIsOpen(true);
    }
  }, []);

  // Auto-close after 5 seconds of inactivity
  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [isOpen, currentStep]);

  const handleClose = () => {
    localStorage.setItem('market4bot-tutorial-seen', 'true');
    setIsOpen(false);
  };

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  if (!isOpen) return null;

  const step = tutorialSteps[currentStep];
  const Icon = step.icon;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full overflow-hidden relative">
        {/* Progress Bar */}
        <div className="flex">
          {tutorialSteps.map((_, idx) => (
            <div
              key={idx}
              className={`h-1 flex-1 transition-all ${
                idx <= currentStep ? 'bg-cyan-500' : 'bg-slate-800'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Close Button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-gray-500 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center`}>
            <Icon className="w-10 h-10 text-white" />
          </div>

          {/* Text */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-3">
              {step.title}
            </h2>
            <p className="text-gray-400">
              {step.description}
            </p>
          </div>

          {/* Step Counter */}
          <p className="text-center text-sm text-gray-500 mb-6">
            Step {currentStep + 1} of {tutorialSteps.length}
          </p>

          {/* Don't show again */}
          <div className="flex items-center justify-center mb-4">
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer hover:text-gray-300">
              <input 
                type="checkbox" 
                className="rounded border-gray-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                onChange={(e) => {
                  if (e.target.checked) {
                    localStorage.setItem('market4bot-tutorial-seen', 'true');
                  }
                }}
              />
              Don't show again
            </label>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                currentStep === 0
                  ? 'text-gray-600 cursor-not-allowed'
                  : 'text-gray-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            <div className="flex gap-2">
              {currentStep < tutorialSteps.length - 1 && (
                <Button variant="secondary" onClick={handleSkip}>
                  Skip
                </Button>
              )}
              <Button onClick={handleNext}>
                {currentStep === tutorialSteps.length - 1 ? (
                  'Get started'
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
